<template>
  <div class="receipt-detail-drawer">
    <finance-drawer
      title="收款单详情"
      width="70%"
      v-bind="$attrs"
      v-on="$listeners"
      :title-no="receiptNo"
      :modal-close="false"
      @open="handleOpen"
      @refresh="handleOpen"
    >
      <div slot="drawer-operation">
        <el-button
          class="table-button table-button-success"
          icon="el-icon-success"
          v-if="$has('FWS-SFKGL-SKD-QR') && handleConfirmShow(detailInfo)"
          @click="handleOperateState(1, detailInfo)"
        >
          确认
        </el-button>
        <el-button
          class="table-button table-button-danger"
          icon="el-icon-error"
          v-if="$has('FWS-SFKGL-SKD-QX') && handleCancelShow(detailInfo)"
          @click="handleCancel(detailInfo)"
        >
          取消
        </el-button>
      </div>
      <div v-loading="loading">
        <div class="main-cont">
          <div class="main-cont-item">
            <h2 class="title-text">
              基本信息
              <icon-status
                :color="detailInfo.verifiedStatus | setStatusColor('receiptStatus')"
                :status="detailInfo.verifiedStatusDesc"
              />
            </h2>
            <el-row :gutter="20" style="margin-top: -8px;">
              <jr-col :data-show="firstCol" :data="detailInfo">
                <template #orderNumber>
                  <jr-tooltip :content="detailInfo.orderNumber" type="popover" />
                </template>
              </jr-col>
              <jr-col :data-show="secondCol" :data="detailInfo" />
              <jr-col :data-show="thirdCol" :data="detailInfo">
                <template #waitDivideAmount>
                  <!-- divideFlag 0-不分账，1-分账 -->
                  <div v-if="detailInfo.divideFlag === 1">{{detailInfo.waitDivideAmount | toFenThousandsAndFloat('¥')}}</div>
                  <div v-else>无需分账</div>
                </template>
              </jr-col>
            </el-row>
          </div>

          <div class="main-cont-item">
            <h2 class="title-text">收款凭证</h2>
            <div>
              <div v-if="!detailInfo.certificate" class="no-certificate">无凭证</div>
              <jr-preview-image v-else :image-list="$stringToArray(detailInfo.certificate)|| []" :is-own="true" />
            </div>
          </div>
          <div class="main-cont-item">
            <h2 class="title-text">关联的应收单</h2>
            <jr-table
              size="mini"
              :tableData="receiptList"
              :tableColumn="receiptTableColumn"
              :pagination="totalCount > 10"
              :total="totalCount"
              :search-params.sync="receiptListParams"
              :loading="receiptListLoading"
              @refresh="refreshList"
            />
          </div>
          <div class="main-cont-item">
            <h2 class="title-text">关联的分账记录</h2>
            <jr-table
              size="mini"
              :tableData="divideRecordsList"
              :tableColumn="subAccountRecordsColumn"
              :pagination="divideRecordsTotalCount > 10"
              :total="divideRecordsTotalCount"
              :search-params.sync="divideRecordsListParams"
              :loading="divideRecordsListLoading"
              @refresh="refreshDivideRecordsList"
            />
          </div>
          <div class="main-cont-item">
            <h2 class="title-text">关联账户余额抵扣</h2>
            <jr-table
              size="mini"
              :tableData="relateDeductDetailData"
              :tableColumn="amountColumn"
            />
          </div>
        </div>
      </div>
    </finance-drawer>

    <!-- 应收单详情 -->
    <receive-detail
      v-if="isReceiveDetailShow"
      :visible.sync="isReceiveDetailShow"
      :receive-no.sync="receiveNo"
    />

    <!-- 确认/取消确认弹窗 -->
    <operate-state
      :visible.sync="isOperateStateShow"
      :receipt-obj.sync="receiptObj"
      :text-config.sync="textConfig"
      @success="handleOperateStateResults()"
    />

    <!-- 金融收款 弹窗确认 -->
    <receiptDialog
      :visible.sync="receiptShow"
      :receiptNo="receiptNo"
      :receipt-obj="receiptObj"
      @success="getDetailData"
    />

    <!-- 取消弹窗 -->
    <jr-operate-dialog
      :visible.sync="isOperateCancelShow"
      :data-obj.sync="receiptObj"
      :text-config.sync="textConfig"
      :loading="cancelLoading"
      @submit="cancelReceipt"
    />
  </div>
</template>

<script>
/**
 * 收款单详情
 * 权限值 FWS-SFKGL-SKD-XQ
 */
import { getReceiptDetail, getDetailReceiptList, getDetailDivideRecords } from '@/api/receivables/receipt';
import receiptMixins from '../receipt/mixins/receipt'; // 收款单模块
import relateDeductDetailMixins from '../../mixins/relateDeduct'; // 关联抵款明细
import { setStatusColor } from '@/filters/index';

export default {
  name: 'ReceiptDetail',
  props: {
    receiptNo: {
      type: String,
      default: '',
    },
    businessNo: {
      type: String,
      default: '',
    },
  },
  mixins: [receiptMixins, relateDeductDetailMixins],
  components: {
    receiveDetail: () => import('./receive-detail'),
  },
  data() {
    return {
      detailInfo: {},
      loading: false,
      isReceiveDetailShow: false, // 详情弹窗
      receiveNo: '', // 应收单编号
      // 基础信息
      firstCol: [
        { label: '客户名称:', prop: 'recBusinessName' },
        { label: '客户编号:', prop: 'recBusinessNo' },
        { label: '收款类型:', prop: 'typeDesc' },
        { label: '业务订单号:', prop: 'orderNumber', slotName: 'orderNumber' },
        { label: '收款单状态:', prop: 'verifiedStatusDesc' },
        { label: '收款时间:', prop: 'receiptTime', filters: 'formatTime' },
        { label: '服务商编号:', prop: 'payeeBusinessNo' },
      ],
      secondCol: [
        { label: '收款单金额:', prop: 'receiptAmount', filters: 'toFenThousandsAndFloat(¥)', className: 'detail-amount_num' },
        { label: '收款方式:', prop: 'receiptTypeDesc' },
        { label: '收款渠道:', prop: 'receiptOrgan' },
        { label: '渠道流水编号:', prop: 'recApplySerialnumber' },
        { label: '申请人:', prop: 'cruser' },
        { label: '收款确认时间:', prop: 'confirmTime', filters: 'formatTime' },
        { label: '服务商名称:', prop: 'payeeBusinessName' },
      ],
      thirdCol: [
        { label: '归属业务:', prop: 'busiSourceDesc' },
        { label: '业务类型:', prop: 'busiTypeDesc' },
        { label: '创建时间:', prop: 'crtime', filters: 'formatTime' },
        { label: '业务BD:', prop: 'bdName' },
        { label: '待分账金额:', prop: 'waitDivideAmount', slotName: 'waitDivideAmount' },
        { label: '操作人:', prop: 'operatorName' },
      ],
      receiptListParams: {
        page: 1,
        limit: 10,
      },
      receiptList: [],
      totalCount: 0,
      receiptListLoading: false,
      // 关联的分账记录
      divideRecordsList: [],
      divideRecordsTotalCount: 0,
      divideRecordsListLoading: false,
      divideRecordsListParams: {
        page: 1,
        limit: 10,
      },
    };
  },
  watch: {
    receiptNo: {
      deep: true,
      handler() {
        if (this.receiptNo) {
          this.handleOpen();
        }
      },
      immediate: true,
    },
  },
  computed: {
    // 应收单表格
    receiptTableColumn() {
      const list = [
        {
          label: '应收单编号',
          prop: 'receiveNo',
          permission: 'FWS-SFKGL-YSD-XQ',
          click: (row) => {
            this.handleReceiveDetail(row);
          },
        }, {
          label: '业务订单编号',
          prop: 'orderNumber',
        }, {
          label: '应收类型',
          prop: 'typeDesc',
        }, {
          label: '应收金额(元)',
          prop: 'shouldRecAmount',
          filterName: 'toFenThousandsAndFloat',
        }, {
          label: '实收金额(元)',
          prop: 'realAmount',
          filterName: 'toFenThousandsAndFloat',
        }, {
          label: '待收金额(元)',
          prop: 'waitRecAmount',
          filterName: 'toFenThousandsAndFloat',
        }, {
          label: '本次收款金额(元)',
          prop: 'currentRecAmount',
          filterName: 'toFenThousandsAndFloat',
        }, {
          label: '状态',
          status: 'receiveStatus',
          statusDesc: 'receiveStatusDesc',
          statusColor: (val) => setStatusColor(val, 'cancelStatus'),
        },
      ];
      return list;
    },
    // 关联的分账记录
    subAccountRecordsColumn() {
      const list = [
        { label: '分账请求编号', prop: 'divideFlowNo' },
        { label: '分账单号', prop: 'divideNo' },
        { label: '分账类型', prop: 'divideTypeDesc' },
        { label: '分账请求时间', prop: 'createTime', width: '115px', filterName: 'formatTime' },
        { label: '客户编号', prop: 'businessNo' },
        { label: '客户名称', prop: 'businessName' },
        { label: '分账金额(元)', prop: 'divideAmount', filterName: 'toFenThousandsAndFloat' },
        {
          label: '状态',
          status: 'status',
          statusDesc: 'statusDesc',
          statusColor: (val) => setStatusColor(val, 'divideStatus'),
        },
      ];
      return list;
    },
    // 关联的账户余额抵扣
    amountColumn() {
      return [
        { label: '抵扣时间', prop: 'createTime', filterName: 'formatTime' },
        { label: '抵扣单号', prop: 'deductDetailNo' },
        { label: '抵扣金额(元)', prop: 'deductAmount', filterName: 'toFenThousandsAndFloat' },
        { label: '业务订单号', prop: 'refundOrderNum' },
        { label: '抵扣类型', prop: 'repayTypeDesc' },
        { label: '备注', prop: 'remark' },
      ];
    },
  },
  methods: {
    init() {
      this.isReceiveDetailShow = false;
      this.getDetailData();
      this._getReceiptList();
      this._getDivideRecordsList();
    },
    async _getReceiptList() {
      try {
        this.receiptListLoading = true;
        const res = await getDetailReceiptList({
          ...this.receiptListParams,
          receiptNo: this.receiptNo,
        });
        this.receiptList = res.data || [];
        this.totalCount = res.totalCount || 0;
        this.receiptListLoading = false;
      } catch (error) {
        console.log(error);
        this.receiptListLoading = false;
      }
    },
    async _getDivideRecordsList() {
      try {
        this.divideRecordsListLoading = true;
        const res = await getDetailDivideRecords({
          ...this.divideRecordsListParams,
          receiptNo: this.receiptNo,
        });
        this.divideRecordsList = res.data || [];
        this.divideRecordsTotalCount = res.totalCount || 0;
        this.divideRecordsListLoading = false;
      } catch (error) {
        console.log(error);
        this.divideRecordsListLoading = false;
      }
    },
    // 刷新列表
    refreshList(params) {
      this.receiptListParams = { ...this.receiptListParams, ...params };
      this._getReceiptList();
    },
    // 刷新列表
    refreshDivideRecordsList(params) {
      this.divideRecordsListParams = { ...this.divideRecordsListParams, ...params };
      this._getDivideRecordsList();
    },
    // 收款单确认/取消确认操作返回结果
    handleOperateStateResults() {
      this.getDetailData();
      this.$emit('operate-success');
    },
    // 应收单详情
    handleReceiveDetail(row) {
      this.receiveNo = row?.receiveNo || '';
      this.isReceiveDetailShow = true;
    },
    handlePictureCardPreview(file, index, gallery) {
      const viewer = this.$refs[gallery].$viewer;
      viewer.view(index);
    },
    // 打开窗口前
    handleOpen() {
      this.receiptListParams = {
        page: 1,
        limit: 10,
      };
      this.detailInfo = {};
      this.$nextTick(() => {
        this.init();
      });
    },
    getDetailData() {
      this.getReceiptDetail();
      this._getRelateDeductDetail({
        businessNo: this.businessNo,
        deductRelationNo: this.receiptNo,
      });
    },

    // 获取详情接口
    async getReceiptDetail() {
      try {
        this.detailInfo = {};
        if (!this.receiptNo) {
          this.$message.warning('收款单编号不能为空！！');
          return;
        }
        this.loading = true;
        const res = await getReceiptDetail({ receiptNo: this.receiptNo });
        this.detailInfo = res.data || {};
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped lang="scss">
.no-certificate {
  font-size: 20px;
  color: #909399;
  text-align: center;
  width: 148px;
  height: 148px;
  border: 1px solid #c0ccda;
  border-radius: 6px;
  margin: 0 8px 8px 0;
  line-height: 148px;
}
.table-button {
  margin-bottom: 0;
}
</style>

